/**
 * CheckInHistory Component
 * Display past check-ins with timeline view
 */

import React, { useState } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { CheckCircle, AlertTriangle, AlertCircle, Star, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, LoadingSpinner, EmptyState, Badge, Avatar } from '../common';
import { useCheckIns } from '../../hooks';
import type { CheckIn, CheckInStatus } from '../../types/okr';
import { CheckInAIAssessment } from '../ai/CheckInAIAssessment';

interface CheckInHistoryProps {
  okrId: string;
}

const statusConfig: Record<CheckInStatus, {
  icon: typeof CheckCircle;
  color: string;
  bgColor: string;
  badgeVariant: 'success' | 'warning' | 'danger';
}> = {
  on_track: {
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    badgeVariant: 'success'
  },
  at_risk: {
    icon: AlertTriangle,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
    badgeVariant: 'warning'
  },
  off_track: {
    icon: AlertCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    badgeVariant: 'danger'
  }
};

export const CheckInHistory: React.FC<CheckInHistoryProps> = ({ okrId }) => {
  const { data, isLoading, error } = useCheckIns(okrId);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        Failed to load check-in history
      </div>
    );
  }

  if (!data?.checkIns || data.checkIns.length === 0) {
    return (
      <EmptyState
        icon={Clock}
        title="No check-ins yet"
        description="Check-ins will appear here once you start tracking your progress"
      />
    );
  }

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Check-in History</h3>

      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

        {/* Check-in items */}
        <div className="space-y-4">
          {data.checkIns.map((checkIn, index) => (
            <CheckInTimelineItem
              key={checkIn.id}
              checkIn={checkIn}
              isFirst={index === 0}
              isExpanded={expandedId === checkIn.id}
              onToggleExpand={() => toggleExpand(checkIn.id)}
            />
          ))}
        </div>
      </div>

      {/* Pagination info */}
      {data.totalPages > 1 && (
        <div className="text-center text-sm text-gray-500 mt-4">
          Showing {data.checkIns.length} of {data.total} check-ins
        </div>
      )}
    </div>
  );
};

interface CheckInTimelineItemProps {
  checkIn: CheckIn;
  isFirst: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const CheckInTimelineItem: React.FC<CheckInTimelineItemProps> = ({
  checkIn,
  isFirst,
  isExpanded,
  onToggleExpand
}) => {
  const config = statusConfig[checkIn.status];
  const StatusIcon = config.icon;

  const hasContent =
    checkIn.accomplishments ||
    checkIn.challenges ||
    checkIn.nextSteps ||
    (checkIn.keyResultNotes && checkIn.keyResultNotes.length > 0) ||
    checkIn.overallSummary;

  return (
    <div className="relative pl-10">
      {/* Timeline dot */}
      <div className={`absolute left-0 w-8 h-8 rounded-full flex items-center justify-center ${
        isFirst ? config.bgColor : 'bg-gray-100'
      }`}>
        <StatusIcon className={`w-4 h-4 ${isFirst ? config.color : 'text-gray-400'}`} />
      </div>

      <Card className={`p-4 ${isFirst ? 'border-primary-200 bg-primary-50/30' : ''}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Avatar
              src={checkIn.user.avatar}
              fallbackText={`${checkIn.user.firstName} ${checkIn.user.lastName}`}
              size="sm"
            />
            <div>
              <span className="font-medium text-sm text-gray-900">
                {checkIn.user.firstName} {checkIn.user.lastName}
              </span>
              <p className="text-xs text-gray-500">
                {format(new Date(checkIn.checkInDate), 'MMM d, yyyy')}
                {' '}({formatDistanceToNow(new Date(checkIn.checkInDate), { addSuffix: true })})
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {checkIn.isOverdue && (
              <Badge variant="danger" className="text-xs">Overdue</Badge>
            )}
            <Badge variant={config.badgeVariant}>
              {checkIn.status.replace('_', ' ')}
            </Badge>
          </div>
        </div>

        {/* Confidence & Progress */}
        <div className="flex items-center gap-4 mb-3 text-sm">
          {checkIn.confidenceLevel && (
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Confidence:</span>
              <div className="flex">
                {[1, 2, 3, 4, 5].map((level) => (
                  <Star
                    key={level}
                    className={`w-3.5 h-3.5 ${
                      level <= checkIn.confidenceLevel!
                        ? 'text-yellow-500 fill-yellow-500'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
          {checkIn.progressPercentage !== undefined && checkIn.progressPercentage !== null && (
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Progress:</span>
              <span className="font-medium text-gray-900">{checkIn.progressPercentage}%</span>
            </div>
          )}
        </div>

        {/* Expandable Content */}
        {hasContent && (
          <>
            <button
              onClick={onToggleExpand}
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Hide details
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Show details
                </>
              )}
            </button>

            {isExpanded && (
              <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
                {checkIn.overallSummary && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Overall Summary</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{checkIn.overallSummary}</p>
                  </div>
                )}
                {checkIn.keyResultNotes && checkIn.keyResultNotes.length > 0 ? (
                  <>
                    {checkIn.keyResultNotes.map((note) => (
                      <div key={note.id} className="border border-gray-100 rounded-lg p-3">
                        <p className="text-sm font-semibold text-gray-900 mb-1">
                          {note.keyResult?.title ?? 'Key Result'}
                        </p>
                        {note.accomplishments && (
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            <span className="text-xs font-medium text-gray-500 uppercase">Accomplishments: </span>
                            {note.accomplishments}
                          </p>
                        )}
                        {note.challenges && (
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            <span className="text-xs font-medium text-gray-500 uppercase">Challenges: </span>
                            {note.challenges}
                          </p>
                        )}
                        {note.nextSteps && (
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            <span className="text-xs font-medium text-gray-500 uppercase">Next Steps: </span>
                            {note.nextSteps}
                          </p>
                        )}
                        {!note.accomplishments && !note.challenges && !note.nextSteps && (
                          <p className="text-xs italic text-amber-700">No notes provided for this KR.</p>
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    {checkIn.accomplishments && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Accomplishments</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{checkIn.accomplishments}</p>
                      </div>
                    )}
                    {checkIn.challenges && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Challenges</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{checkIn.challenges}</p>
                      </div>
                    )}
                    {checkIn.nextSteps && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Next Steps</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{checkIn.nextSteps}</p>
                      </div>
                    )}
                  </>
                )}

                <CheckInAIAssessment
                  checkInId={checkIn.id}
                  assessment={checkIn.aiAssessment ?? null}
                />
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};
